"use client";
import React from "react";
import {
  Box,
  Typography,
  Paper,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Payment as PaymentIcon,
  Security as SecurityIcon,
  AttachMoney as MoneyIcon,
  Description as ContractIcon,
} from "@mui/icons-material";
import CustomInput from "../FormFields/CustomInput";
import CustomSelect from "../FormFields/CustomSelect/CustomSelect";

const contractTypeOptions = [
  { value: "full_time", label: "Full-time" },
  { value: "part_time", label: "Part-time" },
  { value: "zero_hours", label: "Zero Hours" },
  { value: "fixed_term", label: "Fixed Term" },
];

const workTypeOptions = [
  { value: "permanent", label: "Permanent" },
  { value: "temporary", label: "Temporary" },
  { value: "agency", label: "Agency" },
  { value: "contract", label: "Contract" },
];

const travelPaymentOptions = [
  { value: "none", label: "No Payment" },
  { value: "mileage", label: "Mileage Payment" },
  { value: "travel_time", label: "Travel Time Payment" },
  { value: "both", label: "Both Mileage & Time" },
];

const SectionTitle = ({ icon: Icon, children }) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
      <Icon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
      <Typography
        variant="caption"
        fontWeight={700}
        color="#475467"
        sx={{ textTransform: "uppercase", letterSpacing: "0.5px" }}
      >
        {children}
      </Typography>
    </Box>
  );
};

const FieldWrapper = ({ children, basis = "30%", minWidth = "150px" }) => (
  <Box sx={{ flex: `1 1 ${basis}`, minWidth }}>
    {children}
  </Box>
);

const CandidatePayment = ({ handleChange, formData }) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: 2,
        border: "1px solid #E2E8F0",
        bgcolor: "#FFFFFF",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          bgcolor: "#F8FAFC",
          borderBottom: "1px solid #E2E8F0",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "8px",
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <PaymentIcon sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
        </Box>
        <Typography variant="subtitle2" fontWeight={600} color="#1A1A2E">
          Payment & Compliance
        </Typography>
      </Box>

      <Box sx={{ p: 2.5 }}>
        {/* Identity & Compliance */}
        <Box sx={{ mb: 2.5 }}>
          <SectionTitle icon={SecurityIcon}>Identity & Compliance</SectionTitle>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
            <FieldWrapper basis="100%" minWidth="200px">
              <CustomInput
                label="National Insurance Number"
                name="national_insurance"
                value={formData?.national_insurance || ""}
                onChange={handleChange}
                size="small"
                placeholder="e.g., AB123456C"
                fullWidth
              />
            </FieldWrapper>
            <FieldWrapper>
              <CustomInput
                label="Employee Number"
                name="employee_number"
                value={formData?.employee_number || ""}
                onChange={handleChange}
                size="small"
                placeholder="EMP-001"
                fullWidth
              />
            </FieldWrapper>
            <FieldWrapper>
              <CustomInput
                label="NMC Pin Number"
                name="nmc_pin_number"
                value={formData?.nmc_pin_number || ""}
                onChange={handleChange}
                size="small"
                placeholder="NMC-12345"
                fullWidth
              />
            </FieldWrapper>
            <FieldWrapper>
              <CustomInput
                label="Payroll Number"
                name="payroll_number"
                value={formData?.payroll_number || ""}
                onChange={handleChange}
                size="small"
                placeholder="PAY-001"
                fullWidth
              />
            </FieldWrapper>
          </Box>
        </Box>

        {/* Hourly Pay Rates */}
        <Box sx={{ mb: 2.5 }}>
          <SectionTitle icon={MoneyIcon}>Hourly Pay Rates (£)</SectionTitle>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
            <FieldWrapper basis="22%" minWidth="120px">
              <CustomInput
                label="Standard Rate"
                name="hourly_rate"
                type="number"
                value={formData?.hourly_rate || ""}
                onChange={handleChange}
                size="small"
                placeholder="0.00"
                fullWidth
                InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }}>£</Typography> }}
              />
            </FieldWrapper>
            <FieldWrapper basis="22%" minWidth="120px">
              <CustomInput
                label="Weekend Rate"
                name="weekend_rate"
                type="number"
                value={formData?.weekend_rate || ""}
                onChange={handleChange}
                size="small"
                placeholder="0.00"
                fullWidth
                InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }}>£</Typography> }}
              />
            </FieldWrapper>
            <FieldWrapper basis="22%" minWidth="120px">
              <CustomInput
                label="Bank Holiday Rate"
                name="bank_holiday_rate"
                type="number"
                value={formData?.bank_holiday_rate || ""}
                onChange={handleChange}
                size="small"
                placeholder="0.00"
                fullWidth
                InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }}>£</Typography> }}
              />
            </FieldWrapper>
            <FieldWrapper basis="22%" minWidth="120px">
              <CustomInput
                label="Overtime Rate"
                name="overtime_rate"
                type="number"
                value={formData?.overtime_rate || ""}
                onChange={handleChange}
                size="small"
                placeholder="0.00"
                fullWidth
                InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }}>£</Typography> }}
              />
            </FieldWrapper>
          </Box>
        </Box>

        {/* Contractual Information */}
        <Box>
          <SectionTitle icon={ContractIcon}>Contractual Information</SectionTitle>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
            <FieldWrapper>
              <CustomSelect
                options={contractTypeOptions}
                label="Contract Type"
                name="contract_type"
                value={formData?.contract_type || ""}
                onChange={handleChange}
                size="small"
                placeholder="Select type"
                fullWidth
              />
            </FieldWrapper>
            <FieldWrapper>
              <CustomSelect
                options={workTypeOptions}
                label="Work Type"
                name="work_type"
                value={formData?.work_type || ""}
                onChange={handleChange}
                size="small"
                placeholder="Select type"
                fullWidth
              />
            </FieldWrapper>
            <FieldWrapper>
              <CustomInput
                label="Notice Period"
                name="notice_required"
                value={formData?.notice_required || ""}
                onChange={handleChange}
                size="small"
                placeholder="e.g., 1 week"
                fullWidth
              />
            </FieldWrapper>
            <FieldWrapper>
              <CustomInput
                label="Weekly Contracted Hours"
                name="weekly_contracted_hours"
                type="number"
                value={formData?.weekly_contracted_hours || ""}
                onChange={handleChange}
                size="small"
                placeholder="0"
                fullWidth
              />
            </FieldWrapper>
            <FieldWrapper>
              <CustomInput
                label="Max Weekly Hours"
                name="maximum_hours"
                type="number"
                value={formData?.maximum_hours || ""}
                onChange={handleChange}
                size="small"
                placeholder="0"
                fullWidth
              />
            </FieldWrapper>
            <FieldWrapper basis="40%" minWidth="180px">
              <CustomSelect
                options={travelPaymentOptions}
                label="Travel Payment Type"
                name="travel_payment_type"
                value={formData?.travel_payment_type || ""}
                onChange={handleChange}
                size="small"
                placeholder="Select type"
                fullWidth
              />
            </FieldWrapper>
            <FieldWrapper basis="25%" minWidth="120px">
              <CustomInput
                label="Mileage Rate"
                name="mileage_rate"
                type="number"
                value={formData?.mileage_rate || ""}
                onChange={handleChange}
                size="small"
                placeholder="0.45"
                fullWidth
                InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }}>£</Typography> }}
              />
            </FieldWrapper>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default CandidatePayment;